/**
 * RecognitionSuggestions.jsx — M5 (Phase 3 Engagement)
 * ====================================================
 * Côté MANAGER : Homly suggère des agents à reconnaître (signaux positifs de la
 * semaine). En 1 clic, le manager envoie une reconnaissance (notif + bilan agent).
 * Reconnaissance "Manager → Agent" = le levier d'adoption le plus fort en agence.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Award, Send, Sparkles, Loader2 } from 'lucide-react';

export default function RecognitionSuggestions() {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get('/engagement/kudos/suggestions');
                if (alive) setSuggestions(r.data.suggestions || []);
            } catch (e) {
                // silencieux (réservé manager)
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const openCompose = (s) => {
        setOpenId(s.agent_id);
        setDraft(s.suggested_message || '');
    };

    const send = async (s) => {
        if (!draft.trim()) {
            toast.error('Ajoutez un mot de reconnaissance.');
            return;
        }
        setSending(true);
        try {
            await api.post('/engagement/kudos', {
                agent_id: s.agent_id,
                message: draft.trim(),
                reason: s.reason,
            });
            toast.success(`Reconnaissance envoyée à ${s.agent_name}.`);
            setSuggestions((prev) => prev.filter((x) => x.agent_id !== s.agent_id));
            setOpenId(null);
            setDraft('');
        } catch (e) {
            toast.error("L'envoi a échoué. Réessayez.");
        } finally {
            setSending(false);
        }
    };

    if (loading || suggestions.length === 0) return null;

    return (
        <Card className="bg-slate-900 border-[#681793]/30" data-testid="recognition-suggestions">
            <CardHeader className="p-4 sm:p-5 pb-2">
                <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                    <Sparkles className="w-4 h-4 text-[#FF914D]" />
                    Reconnaître vos agents
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                    Un mot de votre part vaut plus que n&apos;importe quel classement.
                </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-5 pt-2 space-y-2.5">
                {suggestions.map((s) => (
                    <div
                        key={s.agent_id}
                        className="rounded-xl bg-card border border-border p-3"
                        data-testid={`recognition-suggestion-${s.agent_id}`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2.5 min-w-0">
                                <div className="w-9 h-9 rounded-xl bg-[#681793]/20 text-[#A855F7] flex items-center justify-center shrink-0">
                                    <Award className="w-4.5 h-4.5" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-white truncate">{s.agent_name}</p>
                                    <p className="text-xs text-muted-foreground leading-snug">{s.reason}</p>
                                </div>
                            </div>
                            {openId !== s.agent_id && (
                                <Button
                                    size="sm"
                                    onClick={() => openCompose(s)}
                                    className="bg-[#681793] hover:bg-[#7d20b0] text-white h-8 shrink-0"
                                    data-testid={`recognize-btn-${s.agent_id}`}
                                >
                                    Reconnaître
                                </Button>
                            )}
                        </div>

                        <AnimatePresence>
                            {openId === s.agent_id && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-3 overflow-hidden"
                                >
                                    <Textarea
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        rows={3}
                                        className="bg-slate-950 border-border text-white text-sm resize-none"
                                        data-testid={`recognition-message-${s.agent_id}`}
                                    />
                                    <div className="flex justify-end gap-2 mt-2">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => { setOpenId(null); setDraft(''); }}
                                            className="h-8 text-muted-foreground"
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => send(s)}
                                            disabled={sending}
                                            className="bg-[#FF914D] hover:bg-[#ff7a26] text-white h-8"
                                            data-testid={`send-recognition-${s.agent_id}`}
                                        >
                                            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                                            Envoyer
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
